import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Loader2, Sparkles, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  applyDocIntelligenceReview,
  getDocIntelligenceMappingRegistry,
  getDocIntelligenceMappings,
  saveDocIntelligenceMappings,
  suggestDocIntelligenceMappings
} from '../../services/api/implementation/impl';
import {
  btnPrimary,
  btnSecondary,
  ConfidenceBar,
  DocIntelPanelHeader,
  DocIntelSkeleton,
  SourceBadge
} from './DocIntelUi';
import { TARGET_TYPE_LABELS } from './docIntelUtils';

function toSavePayload(rows) {
  return rows
    .filter((row) => {
      if (!row.placeholder_name) return false;
      if (row.target_type === 'static') return Boolean(row.static_value?.trim());
      return Boolean(row.target_type && row.target_path?.trim());
    })
    .map((row) => ({
      placeholder_name: row.placeholder_name,
      target_type: row.target_type,
      target_path: row.target_path ?? null,
      static_value: row.static_value ?? null,
      required: row.required ?? false,
      confidence: row.confidence ?? null,
      notes: row.notes ?? null
    }));
}

const DocIntelMappingPanel = ({ reviewSessionId, meta, onApplied }) => {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const registryQuery = useQuery({
    queryKey: ['docIntelligenceRegistry'],
    queryFn: getDocIntelligenceMappingRegistry,
    staleTime: 5 * 60 * 1000
  });

  useEffect(() => {
    if (!reviewSessionId) return;
    let cancelled = false;
    setLoaded(false);
    getDocIntelligenceMappings(reviewSessionId)
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length) {
          setRows(data);
          setLoaded(true);
        } else {
          return suggestDocIntelligenceMappings(reviewSessionId, true).then((suggested) => {
            if (!cancelled) {
              setRows(Array.isArray(suggested) ? suggested : []);
              setLoaded(true);
            }
          });
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to load mappings');
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewSessionId]);

  const suggestMutation = useMutation({
    mutationFn: () => suggestDocIntelligenceMappings(reviewSessionId, true),
    onSuccess: (data) => {
      setRows(Array.isArray(data) ? data : []);
      toast.success('Mappings refreshed');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Suggest failed');
    }
  });

  const saveMutation = useMutation({
    mutationFn: () => saveDocIntelligenceMappings(reviewSessionId, toSavePayload(rows)),
    onSuccess: () => toast.success('Draft saved'),
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Save failed');
    }
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      await saveDocIntelligenceMappings(reviewSessionId, toSavePayload(rows));
      return applyDocIntelligenceReview(reviewSessionId);
    },
    onSuccess: (result) => {
      toast.success('Applied to deal');
      onApplied?.(result);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Apply failed');
    }
  });

  const appColumns = registryQuery.data?.application_columns ?? [];
  const dynamicFieldKeys = registryQuery.data?.dynamic_field_keys ?? [];
  const targetTypes = registryQuery.data?.mapping_target_types ?? [
    'application_column',
    'dynamic_field',
    'static'
  ];

  const stats = useMemo(() => {
    const fromAi = rows.filter((r) => r.source === 'llm').length;
    return { total: rows.length, fromAi, fromRules: rows.length - fromAi };
  }, [rows]);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const isBusy = suggestMutation.isPending || saveMutation.isPending || applyMutation.isPending;

  if (!loaded) {
    return <DocIntelSkeleton rows={3} />;
  }

  return (
    <div className="flex min-h-0 flex-col">
      <DocIntelPanelHeader
        title={meta?.title || 'Map to CRM'}
        subtitle={meta?.subtitle}
        hint={meta?.hint}
        actions={
          <button type="button" disabled={isBusy} onClick={() => suggestMutation.mutate()} className={btnSecondary}>
            {suggestMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 text-teal-700" />
            )}
            Re-suggest
          </button>
        }
      />

      <div className="mx-4 mt-4 flex items-start gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-3 py-2.5 text-sm text-amber-900 sm:mx-5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Review is approved. Confirm mappings below, then apply to update the application and
          regenerate the agreement draft.
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4 sm:p-5">
        {rows.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
            No mappings yet. Try Re-suggest after approving the review.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm ring-1 ring-slate-900/[0.02]">
            <table className="min-w-[720px] w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/90 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2.5">From doc</th>
                  <th className="px-3 py-2.5">Maps to</th>
                  <th className="px-3 py-2.5 w-36">Type</th>
                  <th className="px-3 py-2.5 w-28">Confidence</th>
                  <th className="px-3 py-2.5 w-12 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row, index) => (
                  <tr key={`${row.placeholder_name}-${index}`} className="align-top hover:bg-slate-50/50">
                    <td className="px-3 py-3">
                      <code className="rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-800 ring-1 ring-slate-200">
                        {row.placeholder_name}
                      </code>
                      <div className="mt-1.5">
                        <SourceBadge source={row.source} />
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {row.target_type === 'application_column' ? (
                        <select
                          value={row.target_path || ''}
                          onChange={(e) => updateRow(index, { target_path: e.target.value })}
                          className="w-full min-w-[180px] rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm">
                          <option value="">Select column</option>
                          {appColumns.map((col) => {
                            const path = col.startsWith('Application.') ? col : `Application.${col}`;
                            return (
                              <option key={path} value={path}>
                                {path}
                              </option>
                            );
                          })}
                        </select>
                      ) : row.target_type === 'dynamic_field' ? (
                        <select
                          value={row.target_path || ''}
                          onChange={(e) => updateRow(index, { target_path: e.target.value })}
                          className="w-full min-w-[180px] rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm">
                          <option value="">Select dynamic field</option>
                          {dynamicFieldKeys.map((key) => (
                            <option key={key} value={key}>
                              {key}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={row.target_path || row.static_value || ''}
                          onChange={(e) =>
                            updateRow(
                              index,
                              row.target_type === 'static'
                                ? { static_value: e.target.value }
                                : { target_path: e.target.value }
                            )
                          }
                          className="w-full min-w-[180px] rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm"
                          placeholder={
                            row.target_type === 'static' ? 'Static value' : 'Target path or value'
                          }
                        />
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <select
                        value={row.target_type}
                        onChange={(e) => updateRow(index, { target_type: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm">
                        {targetTypes.map((t) => (
                          <option key={t} value={t}>
                            {TARGET_TYPE_LABELS[t] || t}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      <ConfidenceBar confidence={row.confidence} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                        title="Remove mapping">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 sm:px-5">
        <p className="text-xs text-slate-500">
          {stats.total} mapping{stats.total === 1 ? '' : 's'}
          {stats.total > 0 && ` · ${stats.fromRules} rules · ${stats.fromAi} AI`}
        </p>
        <div className="flex gap-2">
          <button type="button" disabled={isBusy} onClick={() => saveMutation.mutate()} className={btnSecondary}>
            Save draft
          </button>
          <button
            type="button"
            disabled={isBusy || rows.length === 0}
            onClick={() => applyMutation.mutate()}
            className={btnPrimary}>
            {applyMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Apply to deal
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocIntelMappingPanel;
